import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	deleteImportRule,
	loadImportRules,
	moveImportRule,
	saveImportRule,
} from '../../../redux/actions/importRules.js';
import { settingsTabChange } from '../../../redux/actions/settings.js';
import Button from '../../components/Button.jsx';
import Card from '../../components/Card.jsx';
import CircleLoader from '../../components/CircleLoader.jsx';
import ImportRuleEditor from './ImportRuleEditor.jsx';
import { ruleConditions, ruleConditionsText, ruleOutcomeText, ruleShadow } from './importRuleSummary.js';

/** The settings tab this page is, by its slug, and the import page's. */
export const IMPORT_RULES_TAB = 'import-rules';
export const IMPORT_TAB = 'import';

/** Whether the pointer can drag a row: a mouse, not a finger, which has the move buttons instead. */
function canDrag() {
	try {
		return !!window.matchMedia && window.matchMedia('(pointer: fine)').matches;
	} catch(error) {
		return false;
	}
}

/**
 * Every category rule of the budget's Monarch import, in the order they are tried, to read, reorder,
 * edit and add to. The category mapper on the import page writes the simple ones (a category, or a
 * category and merchant); this is where a rule naming an account, a piece of the statement or an
 * amount is made, and where the order that decides between two matching rules is set.
 */
export default function ImportRules() {
	const dispatch = useDispatch();
	const rules = useSelector((state) => state.importRules.rules);
	const availableCategories = useSelector((state) => state.importRules.availableCategories);
	const accountRules = useSelector((state) => state.importRules.accountRules);
	const availableAccounts = useSelector((state) => state.importRules.availableAccounts);
	const matches = useSelector((state) => state.importRules.matches);
	const loading = useSelector((state) => state.importRules.loading);
	const writing = useSelector((state) => state.importRules.writing);
	const loadError = useSelector((state) => state.importRules.error);
	const budgetId = useSelector((state) => {
		const budget = state.dashboard && state.dashboard.player && state.dashboard.player.active_budget;
		return budget ? budget.id : null;
	});

	/** The rule being edited by id, 'new' for one being added, or null. */
	const [editing, setEditing] = useState(null);
	const [listError, setListError] = useState(null);
	const [dragging, setDragging] = useState(null);
	const [dropTarget, setDropTarget] = useState(null);
	/** A rule just saved, to bring into view and mark, until the highlight fades. */
	const [highlighted, setHighlighted] = useState(null);
	/** After a move, the moved rule and which of its buttons was pressed, to put focus back on. */
	const [refocus, setRefocus] = useState(null);
	const listRef = useRef(null);
	const draggable = canDrag();

	/** Arriving from another tab keeps that tab's scroll, which is somewhere down a list of other things. */
	useEffect(() => {
		try {
			window.scrollTo(0, 0);
		} catch(error) {
			/** jsdom has no scrolling. */
		}
	}, []);

	useEffect(() => {
		setEditing(null);
		dispatch(loadImportRules());
	}, [budgetId]);

	/**
	 * A new rule can land anywhere in a list of hundreds, and an edit collapses back into its row, so
	 * the saved rule is scrolled to and marked rather than left for someone to find.
	 */
	useLayoutEffect(() => {
		const row = highlighted !== null && listRef.current
			? listRef.current.querySelector(`[data-rule-id="${highlighted}"]`)
			: null;
		if(!row) {
			return undefined;
		}
		if(row.scrollIntoView) {
			row.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}
		const fade = setTimeout(() => setHighlighted(null), 2500);
		return () => clearTimeout(fade);
	}, [highlighted, rules]);

	/** The buttons are disabled while the move is in flight, which drops focus; it goes back to the moved rule. */
	useLayoutEffect(() => {
		if(!refocus || writing || !listRef.current) {
			return;
		}
		const row = listRef.current.querySelector(`[data-rule-id="${refocus.id}"]`);
		if(row) {
			const same = row.querySelector(`button[data-move="${refocus.direction}"]`);
			const other = row.querySelector(`button[data-move="${refocus.direction === 'up' ? 'down' : 'up'}"]`);
			const target = same && !same.disabled ? same : other;
			if(target) {
				target.focus();
			}
		}
		setRefocus(null);
	}, [refocus, writing, rules]);

	const answered = (result) => {
		setListError(result && !result.saved ? result.error : null);
		return result;
	};

	const move = (rule, position) => {
		if(writing || position < 1 || position > rules.length || position === rule.position) {
			return Promise.resolve();
		}
		setRefocus({ id: rule.id, direction: position < rule.position ? 'up' : 'down' });
		return dispatch(moveImportRule(rule.id, position)).then(answered);
	};

	const remove = (rule) => {
		const confirmed = window.confirm(
			`Delete rule ${rule.position}?\n\n${ruleConditionsText(rule)}\n→ ${ruleOutcomeText(rule, availableCategories)}\n\n`
			+ 'Rows it decided go to the next rule that matches them, or wait for a decision.'
		);
		if(!confirmed) {
			return Promise.resolve();
		}
		return dispatch(deleteImportRule(rule.id)).then(answered);
	};

	const save = (draft) => dispatch(saveImportRule(draft)).then((result) => {
		if(result.saved) {
			setEditing(null);
			setListError(null);
			setHighlighted(result.ruleId || draft.id || null);
		}
		return result;
	});

	const editor = (rule) => (
		<ImportRuleEditor
			rule={rule}
			accountRules={accountRules}
			availableAccounts={availableAccounts}
			availableCategories={availableCategories}
			ruleCount={(rules || []).length}
			onSave={save}
			onCancel={() => setEditing(null)}
		/>
	);

	return (
		<div className='import-settings import-rules-page'>
			<Card>
				<div className='import-rules-head'>
					<h5>Import rules</h5>
					<Button small flat onClick={() => dispatch(settingsTabChange(IMPORT_TAB))}>Back to import</Button>
				</div>
				<p className='grey-text import-lead'>
					Each row from Monarch is checked against these from the top, and the first rule it matches
					decides what it becomes. Put a narrow rule (an account, a merchant, an amount) above a broad
					one, or the broad one catches its rows first. A single transaction&rsquo;s own override, set
					on the import page, still beats every rule.
				</p>
				{!!matches && matches.rowCount > 0 &&
				<p className='grey-text import-rules-matches-total'>
					{matches.rowCount} staged {matches.rowCount === 1 ? 'row' : 'rows'}
					{matches.undecidedCount > 0
						? `, ${matches.undecidedCount} matched by no rule.`
						: ', every one matched by a rule.'}
				</p>
				}

				{!!loadError && <div className='import-error'><span>{loadError}</span></div>}
				{!!listError && <div className='import-error'><span>{listError}</span></div>}

				<div className='import-rules-add'>
					{editing === 'new'
						? editor(null)
						: <Button small disabled={rules === null} onClick={() => setEditing('new')}>Add rule</Button>}
					{loading && <CircleLoader size='small' />}
				</div>

				{rules !== null && !rules.length &&
				<p className='grey-text'>
					No rules yet. Rules are also made on the import page, a category at a time.
				</p>
				}

				{!!rules && rules.length > 0 &&
				<ol className='import-rule-list' ref={listRef}>
					{rules.map((rule) => (
						<li
							key={rule.id}
							data-rule-id={rule.id}
							className={'import-rule'
								+ (highlighted === rule.id ? ' import-rule-highlight' : '')
								+ (dropTarget === rule.id && dragging !== rule.id ? ' import-rule-drop-target' : '')
								+ (dragging === rule.id ? ' import-rule-dragging' : '')}
							draggable={draggable && editing === null && !writing}
							onDragStart={(event) => {
								setDragging(rule.id);
								event.dataTransfer.effectAllowed = 'move';
								event.dataTransfer.setData('text/plain', String(rule.id));
							}}
							onDragOver={(event) => {
								if(dragging !== null) {
									event.preventDefault();
									setDropTarget(rule.id);
								}
							}}
							onDragEnd={() => {
								setDragging(null);
								setDropTarget(null);
							}}
							onDrop={(event) => {
								event.preventDefault();
								const moving = rules.find((candidate) => candidate.id === dragging);
								setDragging(null);
								setDropTarget(null);
								if(moving) {
									move(moving, rule.position);
								}
							}}
						>
							{editing === rule.id
								? editor(rule)
								: <RuleRow
									rule={rule}
									shadow={ruleShadow(rule, rules)}
									count={rules.length}
									availableCategories={availableCategories}
									matches={matches ? matches.byId[rule.id] : null}
									disabled={writing || editing !== null}
									draggable={draggable}
									onMove={(position) => move(rule, position)}
									onEdit={() => setEditing(rule.id)}
									onDelete={() => remove(rule)}
								/>}
						</li>
					))}
				</ol>
				}
			</Card>
		</div>
	);
}

/**
 * One rule as a sentence: its place, what it matches, what it does, and the buttons to change it,
 * with a warning when a rule above takes some or all of its rows first.
 */
export function RuleRow({ rule, shadow, count, availableCategories, matches, disabled, draggable, onMove, onEdit, onDelete }) {
	const conditions = ruleConditions(rule);
	return (
		<div className='import-rule-row'>
			<span className='import-rule-position'>
				{rule.position}
			</span>
			<div className='import-rule-text'>
				<div className='import-rule-conditions'>
					{conditions.length
						? conditions.map((part, index) => (
							<React.Fragment key={index}>
								{index > 0 && <span className='grey-text'> · </span>}
								<span>{part}</span>
							</React.Fragment>
						))
						: <span>Every row</span>}
				</div>
				<div className={'import-rule-outcome' + (rule.category_deleted ? ' red-text' : '')}>
					&rarr; {ruleOutcomeText(rule, availableCategories)}
				</div>
				{!!matches &&
				<div className='import-rule-matches grey-text'>{matchesText(matches)}</div>
				}
				{!!shadow &&
				<div className='import-rule-shadow'>
					<span>{shadow.text}</span>
					{shadow.moveTo !== null &&
					<Button small flat disabled={disabled} onClick={() => onMove(shadow.moveTo)}>
						Move above rule {shadow.moveTo}
					</Button>
					}
				</div>
				}
			</div>
			<div className='import-rule-actions'>
				<Button
					small
					flat
					aria-label={`Move rule ${rule.position} up`}
					data-move='up'
					title='Move up'
					disabled={disabled || rule.position <= 1}
					onClick={() => onMove(rule.position - 1)}
				>
					<i className='material-icons'>arrow_upward</i>
				</Button>
				<Button
					small
					flat
					aria-label={`Move rule ${rule.position} down`}
					data-move='down'
					title='Move down'
					disabled={disabled || rule.position >= count}
					onClick={() => onMove(rule.position + 1)}
				>
					<i className='material-icons'>arrow_downward</i>
				</Button>
				<Button small flat disabled={disabled} onClick={onEdit}>Edit</Button>
				<Button small flat danger disabled={disabled} onClick={onDelete}>Delete</Button>
			</div>
		</div>
	);
}

/** "Decides 3 staged rows", or why it decides fewer than it matches. */
export function matchesText({ matches, decides }) {
	if(!matches) {
		return 'Matches no staged rows';
	}
	const rows = (n) => `${n} staged ${n === 1 ? 'row' : 'rows'}`;
	if(decides === matches) {
		return `Decides ${rows(decides)}`;
	}
	if(!decides) {
		return `Matches ${rows(matches)}, all caught by a rule above`;
	}
	return `Decides ${decides} of the ${rows(matches)} it matches; a rule above catches the rest`;
}
